import React, { Fragment, useContext, useEffect, useState } from 'react'
import { useLocation } from 'react-router'
import { createUseStyles } from 'react-jss'
import { Theme } from '../../theme'
import {
  LayoutContainer,
  useLayoutStyles,
} from '../../components/LayoutContainer'
import { TabBar } from '../../components/TabBar'
import { Link } from 'react-router-dom'
import { FeatherIcon } from '../../components/icons/FeatherIcon'
import { Button } from '../../components/Button'
import { classNames } from '../../utils'
import { DoingBusinessWithQPageContext } from './DoingBusinessWithQPage'
import { Media } from '../../components/Media'

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    backgroundColor: theme.kitColors.grey100,
  },
  splitLayoutContainer: {
    gridColumn: 'span 12',
    [theme.breakpoints.down('md')]: {
      gridColumn: 'span 8',
    },
    [theme.breakpoints.down('sm')]: {
      gridColumn: 'span 4',
    },
    '& > *:first-child': {
      justifyContent: 'flex-start',
    },
  },
  headerContainer: {
    gridColumn: '1 / span 8',
    marginBottom: theme.spacing.XXXL,
    [theme.breakpoints.down('md')]: {
      gridColumn: '1 / span 6',
      marginBottom: theme.spacing.XXL,
    },
    [theme.breakpoints.down('sm')]: {
      gridColumn: 'span 4',
    },
  },
  h3: {
    ...theme.typography.h3,
    marginBottom: theme.spacing.M,
  },
  h5: {
    ...theme.typography.h5,
    marginBottom: theme.spacing.M,
  },
  smallBody: {
    ...theme.typography.smallBody,
    color: theme.kitColors.grey700,
  },
  text: {
    ...theme.typography.body,
    color: theme.kitColors.grey700,
  },
  tabBar: {
    gridColumn: 'span 12',
    '& > *': {
      marginBottom: theme.spacing.XXL,
    },
    [theme.breakpoints.down('md')]: {
      gridColumn: 'span 8',
    },
    [theme.breakpoints.down('sm')]: {
      gridColumn: 'span 4',
    },
    display: 'flex',
    [theme.breakpoints.down('md')]: {
      flexFlow: 'row wrap',
      justifyContent: 'center',
    },
  },
  iconTextContainer: {
    display: 'grid',
    gridTemplateColumns: '24px 1fr',
    rowGap: theme.spacing.M,
    columnGap: theme.spacing.M,
    marginBottom: theme.spacing.XXL,
  },
  icon: {
    marginTop: theme.spacing.XS,
  },
  link: {
    ...theme.traits.clickable,
    ...theme.typography.menuLink,
    textDecoration: 'none',
    color: theme.colors.blue,
    marginLeft: theme.spacing.L,
  },
  linkRow: {
    display: 'flex',
    alignItems: 'center',
  },
  image: {
    '& > img': {
      height: 'auto',
    },
  },
}))

interface locationState {
  scrollTo: string
}

export const DoingBusinessWithQRetailerRequirementsSection: React.FC = () => {
  const classes = useStyles()
  const layoutClasses = useLayoutStyles()
  const [tabIndex, setTabIndex] = useState(0)
  const context = useContext(DoingBusinessWithQPageContext)

  const location = useLocation<locationState>()?.state
  let retailerRef: HTMLDivElement | null = null
  useEffect(() => {
    if (location?.scrollTo === 'retailer') {
      retailerRef?.scrollIntoView({ behavior: 'smooth' })
    }
  })

  const requirementTabs = context?.entry?.retailerSectionRequirementTabs ?? []
  const requirementDescription =
    requirementTabs[tabIndex]?.description?.split('\n') ?? []
  const media = requirementTabs[tabIndex]?.media?.find((img) => img != null)

  return (
    <>
      <div
        ref={(ref) => {
          retailerRef = ref
        }}
      />
      <LayoutContainer
        containerClass={classNames(
          layoutClasses.paddedContainerLarge,
          classes.container,
        )}
        contentClass={layoutClasses.gridLayout}
      >
        <div className={classes.headerContainer}>
          <h2 className={classes.h3}>
            {context?.entry?.retailerSectionHeader}
          </h2>
          <p className={classes.text}>
            {context?.entry?.retailerSectionDescription}
          </p>
        </div>
        <TabBar
          className={classes.tabBar}
          tabs={requirementTabs?.map((tab) => tab?.header ?? '')}
          selected={tabIndex}
          onChange={setTabIndex}
        />
        <div
          className={classNames(
            classes.splitLayoutContainer,
            layoutClasses.splitLayoutLeftContent,
          )}
        >
          <div>
            <h3 className={classes.h5}>{requirementTabs[tabIndex]?.header}</h3>
            <div className={classes.iconTextContainer}>
              {requirementDescription.map((text, index) => (
                <Fragment key={index}>
                  <FeatherIcon name={'check'} className={classes.icon} />
                  <p className={classes.smallBody}>{text}</p>
                </Fragment>
              ))}
            </div>
            <div className={classes.linkRow}>
              <Link
                to={{
                  pathname: context?.entry?.retailerSectionStartNowLink ?? '',
                }}
                target="_blank"
              >
                <Button>Retailers Start Now</Button>
              </Link>
              <a href="/contact" className={classes.link}>
                Questions?
              </a>
            </div>
          </div>
          <Media
            src={media?.url}
            type={media?.mimeType}
            alt={media?.altText}
            credit={media?.credit}
            className={classes.image}
          />
        </div>
      </LayoutContainer>
    </>
  )
}
